import React, {useRef} from 'react';
import PositivityFeedbackCell from "../positivity/PositivityFeedbackCell";
import { API } from "aws-amplify";
import { toast } from "react-toastify";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import "react-responsive-carousel/lib/styles/carousel.min.css"; // requires a loader
import { Carousel } from 'react-responsive-carousel';
import Masonry from "react-masonry-css";
import useInterval from 'beautiful-react-hooks/useInterval';

import { getLocalDateStringFromUTC } from "@/common/LocalTimeStamp";
import  { hideAuthorsFromFocusBoard } from "@/shared/access";
import SurveyResults from "../survey/SurveyResults";
import _ from "lodash";
import useDocumentTitle from "../../common/UseDocumentTitle";

function PositivityIndex() {
    useDocumentTitle('PowerEngage - Feedback');
    const LIST_TAB = "LIST";
    const FOCUS_TAB = "FOCUS";
    const FULL_SCREEN_CLASS = "fullScreen";
    const [feedback, setFeedback] = React.useState([]);
    const [lastFeedbackLoadedAt, setLastFeedbackLoadedAt] = React.useState(0);
    const [activeTab, setActiveTab] = React.useState(LIST_TAB);

    // Create refs for each transition item
    const nodeRefs = React.useRef({});
    const [currentSlide, setCurrentSlide] = React.useState(0);
    const carouselRef = React.useRef(null);
    const titleRef = React.useRef(null);
    const leftArrowRef = React.useRef(null);
    const rightArrowRef = React.useRef(null);
    const announcementRef = React.useRef(null);
    const listButtonRef = React.useRef(null);
    const focusedButtonRef = React.useRef(null);

    React.useEffect(() => {
        document.body.classList.remove(FULL_SCREEN_CLASS);
        if (activeTab === FOCUS_TAB) {
            document.body.classList.add(FULL_SCREEN_CLASS);
        }
    }, [activeTab])

    // Handle slide change to announce content
    const handleSlideChange = (index) => {
        shouldAnnounceSlideChange.current = true;
        setCurrentSlide(index);
    };

    // Track last announcement to prevent duplicates
    const lastAnnouncementRef = React.useRef('');
    const announcementTimeoutRef = React.useRef(null);

    // Announce slide content for screen readers
    const announceSlideContent = (index) => {
        if (!feedback.items || feedback.items.length === 0) return;

        const f = feedback.items[index];
        if (!f) return;

        let personnelInfo = '';
        if (f.personnel && f.feedbackSubject && !hideAuthorsFromFocusBoard()) {
            let names = [];
            if (f.feedbackSubject === "CALLTAKER") {
                names = f.personnel.filter(ep => ep.type === "CALLTAKER").map(ct => ct.name);
            } else if (f.feedbackSubject === "RESPONDER") {
                names = f.personnel.filter(ep => ep.type === "RESPONDER").map(resp => resp.name);
            } else if (f.feedbackSubject === "ALL") {
                names = f.personnel.map(p => p.name);
            }
            if (names.length > 0) {
                personnelInfo = `, Personnel: ${names.join(', ')}`;
            }
        }

        const dateInfo = f.date ? getLocalDateStringFromUTC(f.date, "MM/DD/YYYY, h:mm:ss a") : '';
        const announcement = `Item ${index + 1} of ${feedback.items.length}. Date: ${dateInfo}. Feedback: ${f.feedback}${personnelInfo}`;

        // Prevent duplicate announcements
        if (lastAnnouncementRef.current === announcement) {
            return;
        }

        // Clear any pending announcement
        if (announcementTimeoutRef.current) {
            clearTimeout(announcementTimeoutRef.current);
        }

        if (announcementRef.current) {
            // Clear first
            announcementRef.current.textContent = '';
            // Then announce after a brief delay
            announcementTimeoutRef.current = setTimeout(() => {
                if (announcementRef.current) {
                    announcementRef.current.textContent = announcement;
                    lastAnnouncementRef.current = announcement;
                }
            }, 100);
        }
    };

    // Announce title when entering focused view
    const announceFeedbackBoardTitle = () => {
        // Clear any pending announcements
        if (announcementTimeoutRef.current) {
            clearTimeout(announcementTimeoutRef.current);
        }

        if (announcementRef.current) {
            // Clear previous content first
            lastAnnouncementRef.current = '';
            announcementRef.current.textContent = '';

            announcementTimeoutRef.current = setTimeout(() => {
                if (announcementRef.current) {
                    announcementRef.current.textContent = 'Feedback Board';
                    // After announcing title, announce the first item
                    announcementTimeoutRef.current = setTimeout(() => {
                        if (feedback.items && feedback.items.length > 0) {
                            lastAnnouncementRef.current = '';
                            announceSlideContent(currentSlide);
                        }
                    }, 1000);
                }
            }, 200);
        }
    };

    // Announce title when entering list view
    const announceFeedbackBoardTitleForList = () => {
        // Clear any pending announcements
        if (announcementTimeoutRef.current) {
            clearTimeout(announcementTimeoutRef.current);
        }

        if (announcementRef.current) {
            // Clear previous content first
            lastAnnouncementRef.current = '';
            announcementRef.current.textContent = '';

            announcementTimeoutRef.current = setTimeout(() => {
                if (announcementRef.current) {
                    announcementRef.current.textContent = 'Feedback Board';
                }
            }, 200);
        }
    };

    // Keyboard navigation handler
    const handleCarouselKeyDown = (e, onPrev, onNext) => {
        if (e.key === 'ArrowLeft' || (e.key === 'Tab' && e.shiftKey)) {
            e.preventDefault();
            if (onPrev) onPrev();
        } else if (e.key === 'ArrowRight' || e.key === 'Tab') {
            e.preventDefault();
            if (onNext) onNext();
        } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            // Enter key behavior can be customized if needed
        }
    };

    const styles = {
        container: {
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
        }
    };

    const backgroundColor1 = {
        backgroundColor: "#6550B9"
    }
    const backgroundColor2 = {
        background: "#00A2C7"
    }
    const backgroundColor3 = {
        background: "#0D74CE"
    }

    const colors = [
        backgroundColor1,
        backgroundColor2,
        backgroundColor3
    ]

    const loadFeedback = (since) => {
        let params = '';

        if (since > 0) {
            params += '?since=' + since;
        }
        API.get('Core', '/api/v1/feedback-board' + params)
            .then(response => {
                    if (response.status !== "NO_NEW_DATA") {
                        setFeedback(Object.assign([], response));
                        if(feedback.lastUpdated) {
                            const timestamp = new Date(feedback.lastUpdated).getTime();
                            setLastFeedbackLoadedAt(timestamp);
                        }
                    }
                },
                error => {
                    toast("Could not load feedback: " + error.response.data.message, { position: "top-center", type: "error" });
                }
            );
    }

    useInterval(() => {
        loadFeedback(lastFeedbackLoadedAt);
    }, 3600000);

    React.useEffect(() => {
        loadFeedback(lastFeedbackLoadedAt);
    }, [])

    // Track if we should announce slide changes (not during initial load or tab focus)
    const shouldAnnounceSlideChange = React.useRef(false);

    React.useEffect(() => {
        // Only announce if this is from carousel auto-scroll or arrow click
        if (activeTab === FOCUS_TAB &&
            feedback.items &&
            feedback.items.length > 0 &&
            shouldAnnounceSlideChange.current) {
            // This is a carousel slide change from auto-scroll or arrow click
            setTimeout(() => {
                announceSlideContent(currentSlide);
            }, 200);
        }
    }, [currentSlide]);

    const renderCustomPrevArrow = (onClickHandler, hasPrev, label) =>
        hasPrev && (
            <button type="button"
                    ref={leftArrowRef}
                    className="control-arrow control-prev"
                    tabIndex="0"
                    onClick={onClickHandler}
                    onFocus={() => {
                        // Disable auto-announcements temporarily
                        shouldAnnounceSlideChange.current = false;
                        // Announce current item when arrow receives focus
                        setTimeout(() => {
                            if (feedback.items && feedback.items.length > 0) {
                                announceSlideContent(currentSlide);
                            }
                        }, 100);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onClickHandler();
                        } else if (e.key === 'Tab' && !e.shiftKey) {
                            // Tab to right arrow
                            e.preventDefault();
                            if (rightArrowRef.current) {
                                rightArrowRef.current.focus();
                            }
                        } else if (e.key === 'Tab' && e.shiftKey) {
                            // Shift+Tab to focused button
                            e.preventDefault();
                            if (focusedButtonRef.current) {
                                focusedButtonRef.current.focus();
                            }
                        }
                    }}
                    title={label}
                    aria-label={label}>
            </button>
        );

    const renderCustomNextArrow = (onClickHandler, hasNext, label) =>
        hasNext && (
            <button type="button"
                    ref={rightArrowRef}
                    className="control-arrow control-next"
                    tabIndex="0"
                    onClick={onClickHandler}
                    onFocus={() => {
                        // Disable auto-announcements temporarily
                        shouldAnnounceSlideChange.current = false;
                        // Announce current item when arrow receives focus
                        setTimeout(() => {
                            if (feedback.items && feedback.items.length > 0) {
                                announceSlideContent(currentSlide);
                            }
                        }, 100);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onClickHandler();
                        } else if (e.key === 'Tab' && !e.shiftKey) {
                            // Tab to list button
                            e.preventDefault();
                            if (listButtonRef.current) {
                                listButtonRef.current.focus();
                            }
                        } else if (e.key === 'Tab' && e.shiftKey) {
                            // Shift+Tab to left arrow
                            e.preventDefault();
                            if (leftArrowRef.current) {
                                leftArrowRef.current.focus();
                            }
                        }
                    }}
                    title={label}
                    aria-label={label}>
            </button>
        );

    return (
        <div className="rootView positivityView">
            {/* Screen reader announcement region */}
            <div
                ref={announcementRef}
                role="status"
                aria-live="assertive"
                aria-atomic="true"
                className="sr-only"
                style={{
                    position: 'absolute',
                    left: '-10000px',
                    width: '1px',
                    height: '1px',
                    overflow: 'hidden'
                }}
            />

            <div className="navBar">
                <h1
                    className="title"
                    ref={titleRef}
                    tabIndex="-1"
                    id="feedback-board-title"
                >
                    Feedback Board
                </h1>

                <div className="actions" role="tablist">
                    <div className={`segmentedBar index${activeTab === LIST_TAB ? '0' : '1'}`} style={{ width: "172px" }}>
                        <div
                            ref={listButtonRef}
                            onClick={() => {
                                setActiveTab(LIST_TAB);
                                announceFeedbackBoardTitleForList();
                            }}
                            className={`segment ${activeTab === LIST_TAB ? ' selected' : ''}`}
                            tabIndex="0"
                            role="tab"
                            aria-selected={activeTab === LIST_TAB}
                            aria-label="List view"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setActiveTab(LIST_TAB);
                                    announceFeedbackBoardTitleForList();
                                } else if (e.key === 'Tab' && !e.shiftKey) {
                                    // Tab to focused button
                                    e.preventDefault();
                                    if (focusedButtonRef.current) {
                                        focusedButtonRef.current.focus();
                                    }
                                } else if (e.key === 'Tab' && e.shiftKey) {
                                    // Shift+Tab to right arrow (only in focus view)
                                    if (activeTab === FOCUS_TAB) {
                                        e.preventDefault();
                                        if (rightArrowRef.current) {
                                            rightArrowRef.current.focus();
                                        }
                                    }
                                }
                            }}
                        >
                            <div className="title">List</div>
                        </div>

                        <div
                            ref={focusedButtonRef}
                            onClick={() => {
                                setActiveTab(FOCUS_TAB);
                                announceFeedbackBoardTitle();
                            }}
                            className={`segment ${activeTab === FOCUS_TAB ? ' selected' : ''}`}
                            tabIndex="0"
                            role="tab"
                            aria-selected={activeTab === FOCUS_TAB}
                            aria-label="Focused view"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setActiveTab(FOCUS_TAB);
                                    announceFeedbackBoardTitle();
                                } else if (e.key === 'Tab' && !e.shiftKey) {
                                    // Tab to left arrow (only in focus view)
                                    if (activeTab === FOCUS_TAB) {
                                        e.preventDefault();
                                        if (leftArrowRef.current) {
                                            leftArrowRef.current.focus();
                                        }
                                    }
                                } else if (e.key === 'Tab' && e.shiftKey) {
                                    // Shift+Tab to list button
                                    e.preventDefault();
                                    if (listButtonRef.current) {
                                        listButtonRef.current.focus();
                                    }
                                }
                            }}
                        >
                            <div className="title">Focused</div>
                        </div>
                        <div className="selection" />
                    </div>
                </div>

                <div className="separator"></div>
            </div>
            {
                activeTab === LIST_TAB ? (
                    <div className="scrollView">
                        <TransitionGroup className="feedbackList tableView">
                            <Masonry className={"feedback-masonry"} breakpointCols={2} columnClassName={"feedback-masonry-column"}>
                                {
                                    _.map(feedback.items, function (f, i) {
                                        let color = colors[(i % 3)];

                                        // Create or retrieve ref for this item
                                        if (!nodeRefs.current[f.id]) {
                                            nodeRefs.current[f.id] = React.createRef();
                                        }
                                        const nodeRef = nodeRefs.current[f.id];

                                        return (
                                            <CSSTransition
                                                key={f.id}
                                                nodeRef={nodeRef}
                                                timeout={5000}
                                                classNames="feedback-item"
                                            >
                                                <div ref={nodeRef}>
                                                    <SurveyResults surveyInstanceId={f.surveyInstanceId} onReviewChanged={() => loadFeedback(0)}>
                                                        <PositivityFeedbackCell
                                                            extraClasses={"clickable-feedback"}
                                                            feedbackStyle={color}
                                                            feedbackText={f.feedback}
                                                            surveyId={f.surveyId}
                                                            feedbackSubject={f.feedbackSubject}
                                                            encounterPersonnel={f.encounterPersonnel}
                                                        />
                                                    </SurveyResults>
                                                </div>
                                            </CSSTransition>
                                        )
                                    })
                                }
                            </Masonry>
                        </TransitionGroup>
                    </div>
                ) : (
                    <div className="focusView">
                        <div className={"feedbackList tableView"}
                             role="region"
                             aria-labelledby="feedback-board-title">
                            <Carousel
                                ref={carouselRef}
                                onChange={handleSlideChange}
                                renderArrowPrev={(onClickHandler, hasPrev) =>
                                    renderCustomPrevArrow(onClickHandler, hasPrev, 'Previous feedback item')
                                }
                                renderArrowNext={(onClickHandler, hasNext) =>
                                    renderCustomNextArrow(onClickHandler, hasNext, 'Next feedback item')
                                }
                                centerMode={false}
                                showArrows={true}
                                showStatus={false}
                                showIndicators={false}
                                infiniteLoop={true}
                                showThumbs={false}
                                autoPlay={true}
                                interval={10000}
                                transitionTime={600}
                                stopOnHover={false}
                            >
                                {
                                    _.map(feedback.items, function (f, i) {
                                        let color = colors[(i % 3)];
                                        let responders, all, callTakers, responderNames, callTakerNames, allNames = new Set();

                                        if (f.personnel != null) {
                                            responders = new Set(f.personnel.filter(ep => ep.type === "RESPONDER").map(resp => resp.name));
                                            callTakers = new Set(f.personnel.filter(ep => ep.type === "CALLTAKER").map(ct => ct.name));
                                            all = new Set(f.personnel.map(p => p.name));
                                            responderNames = Array.from(responders);
                                            callTakerNames = Array.from(callTakers);
                                            allNames = Array.from(all);
                                        }

                                        // Build accessible label
                                        let accessibleLabel = `Feedback item ${i + 1} of ${feedback.items.length}`;
                                        let personnelLabel = '';

                                        if (f.personnel && f.feedbackSubject && !hideAuthorsFromFocusBoard()) {
                                            let names = [];
                                            if (f.feedbackSubject === "CALLTAKER") {
                                                names = Array.from(new Set(f.personnel.filter(ep => ep.type === "CALLTAKER").map(ct => ct.name)));
                                            } else if (f.feedbackSubject === "RESPONDER") {
                                                names = Array.from(new Set(f.personnel.filter(ep => ep.type === "RESPONDER").map(resp => resp.name)));
                                            } else if (f.feedbackSubject === "ALL") {
                                                names = Array.from(new Set(f.personnel.map(p => p.name)));
                                            }
                                            if (names.length > 0) {
                                                personnelLabel = names.join(', ');
                                            }
                                        }

                                        const dateLabel = f.date ? getLocalDateStringFromUTC(f.date, "MM/DD/YYYY, h:mm:ss a") : '';

                                        return (
                                            <div style={Object.assign(color)}
                                                 className="feedbackCell focusedFeedback"
                                                 key={f.surveyAnswerId}
                                                 tabIndex="-1"
                                                 role="article"
                                                 aria-label={accessibleLabel}
                                            >
                                                {f.feedback != null && f.feedback.length >= 400 ?
                                                    <div className="feedbackText small" aria-label={`Feedback: ${f.feedback}`}>{f.feedback}</div> :
                                                    f.feedback != null && f.feedback.length >= 200 && f.feedback.length < 400 ?
                                                        <div className="feedbackText medium" aria-label={`Feedback: ${f.feedback}`}>{f.feedback}</div> :
                                                        <div className="feedbackText big" aria-label={`Feedback: ${f.feedback}`}>{f.feedback}</div>
                                                }
                                                <div style={styles.container}>
                                                    <span className="feedbackDate" aria-label={`Date: ${dateLabel}`}>
                                                        {dateLabel}
                                                    </span>
                                                    {
                                                        f.personnel && f.feedbackSubject && f.feedbackSubject === "CALLTAKER" && !hideAuthorsFromFocusBoard() ? (
                                                            <p className="feedbackAuthor"
                                                               style={{fontSize: '20px'}}
                                                               aria-label={`Personnel: ${callTakerNames.join(', ')}`}>
                                                                {callTakerNames.join(' - ')}
                                                            </p>
                                                        ) : null
                                                    }
                                                    {
                                                        f.personnel && f.feedbackSubject && f.feedbackSubject === "RESPONDER" && !hideAuthorsFromFocusBoard() ? (
                                                            <p className="feedbackAuthor"
                                                               style={{fontSize: '20px'}}
                                                               aria-label={`Personnel: ${responderNames.join(', ')}`}>
                                                                {responderNames.join(' - ')}
                                                            </p>
                                                        ) : null
                                                    }
                                                    {
                                                        f.personnel && f.feedbackSubject && f.feedbackSubject === "ALL" && !hideAuthorsFromFocusBoard() ? (
                                                            <p className="feedbackAuthor"
                                                               style={{fontSize: '20px'}}
                                                               aria-label={`Personnel: ${allNames.join(', ')}`}>
                                                                {allNames.join(' - ')}
                                                            </p>
                                                        ) : null
                                                    }
                                                </div>
                                            </div>
                                        )
                                    })
                                }
                            </Carousel>
                        </div>
                    </div>
                )
            }
        </div>
    );
}

export default (PositivityIndex);